import React, { useEffect, useState, Fragment, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Listbox, Dialog, Transition, Tooltip } from "@headlessui/react";
import { FiSearch, FiChevronDown } from "react-icons/fi";
import {
  CheckCircleIcon,
  PlayCircleIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/solid";
import { v4 as uuidv4 } from "uuid";
import scannerApi from "../apis/scanner.api";
import MetaData from "../component/Metadata";
const BRAND = "#1d275f";

export default function EventBookingListPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialEventId = searchParams.get("content_id") || "";

  const [eventId, setEventId] = useState(initialEventId);
  const [page, setPage] = useState(1);
  const [perPage] = useState(50);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [errorBanner, setErrorBanner] = useState(null);

  const [selectedBooking, setSelectedBooking] = useState(null);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [toast, setToast] = useState(null);

  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [showError, setShowError] = useState(false);
  const [additionalData, setAdditionalData] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedCount, setSelectedCount] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState(null);
  const [autoDismissSeconds, setAutoDismissSeconds] = useState(0);
  const [autoProgress, setAutoProgress] = useState(100);
  const autoTimerRef = useRef(null);
  const autoStartRef = useRef(null);
  const confirmKeyRef = useRef(null);

  const showToast = (type, message, timeout = 2500) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), timeout);
  };

  const autoConfirmRef = useRef(false);

  useEffect(() => {
    if (!verifyOpen) {
      setSelectedCount(1);
      autoConfirmRef.current = false;
      return;
    }

    if (verifyOpen && selectedBooking) {
      const { qty, used, remaining } = computeScanInfo(selectedBooking);

      setSelectedCount(Math.max(1, remaining));

      if (remaining === 1 && !autoConfirmRef.current) {
        autoConfirmRef.current = true;

        setTimeout(() => {
          if (verifyOpen && !verifying) {
            confirmManualVerify();
          }
        }, 700);
      }
    }
  }, [verifyOpen, selectedBooking]);

  const buildParams = () => ({
    page,
    limit: perPage,
    search: search || undefined,
  });

  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState(null);
  const [eventSearch, setEventSearch] = useState("");

  const fetchEvents = async () => {
    setEventsLoading(true);
    setEventsError(null);
    try {
      scannerApi.eventList({
        params: { page: 1, limit: 100, search: eventSearch || undefined },
        success: (res) => {
          const payload = res?.data || {};
          const list = payload?.data || [];
          setEvents(list);
        },
        error: (err) => {
          console.error("Failed to load events:", err);
          setEventsError("Unable to load events");
        },
      });
    } catch (e) {
      console.error(e);
      setEventsError("Unexpected error");
    } finally {
      setEventsLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();

    return () => {
      if (autoTimerRef.current) {
        clearInterval(autoTimerRef.current);
        autoTimerRef.current = null;
      }
      autoStartRef.current = null;
    };
  }, []);

  const fetchBookings = async () => {
    if (!eventId) {
      setBookings([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    setErrorBanner(null);

    try {
      scannerApi.listBookingsForEvent({
        eventId,
        params: buildParams(),
        success: (res) => {
          const payload = res?.data || {};
          const data = payload?.data || [];
          const meta = payload?.meta || { total: data.length };
          setBookings(data);
          setTotal(meta.total || data.length);
        },
        error: (err) => {
          console.error("listBookingsForEvent error:", err);
          setErrorBanner(
            err?.response?.data?.message || "Failed to load bookings."
          );
        },
      });
    } catch (e) {
      console.error(e);
      setErrorBanner("Unexpected error while fetching bookings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, [eventId, page, search]);

  const computeScanInfo = (b) => {
    const qty = Number(b?.quantity ?? 0);
    const used = Number(b?.scan_count ?? 0);
    const remaining = Math.max(0, qty - used);
    const exhausted = used >= qty;
    const overscanned = used > qty;
    return { used, qty, remaining, exhausted, overscanned };
  };

  const ScanInfoMobile = ({ booking }) => {
    const { used, qty, remaining, exhausted, overscanned } =
      computeScanInfo(booking);

    if (overscanned) {
      return (
        <div className="mt-2 flex items-center text-xs gap-1 text-orange-700">
          <ExclamationTriangleIcon className="w-4 h-4 text-orange-600" />
          <span className="font-medium">Scans:</span>
          <span className="ml-1">
            {used} / {qty} — Over Scanned
          </span>
        </div>
      );
    }

    if (exhausted) {
      return (
        <div className="mt-2 flex items-center text-xs gap-1 text-gray-700">
          <CheckCircleIcon className="w-4 h-4 text-gray-600" />
          <span className="font-medium">Scans:</span>
          <span className="ml-1">
            {used} / {qty} — All Used
          </span>
        </div>
      );
    }

    return (
      <div className="mt-2 flex items-center text-xs gap-1 text-green-700">
        <PlayCircleIcon className="w-4 h-4 text-green-600" />
        <span className="font-medium">Scans:</span>
        <span className="ml-1">
          {used} / {qty} — {remaining} Left
        </span>
      </div>
    );
  };

  const openVerifyModal = (booking) => {
    confirmKeyRef.current = null;
    setSelectedBooking(booking);
    setVerifyOpen(true);
  };

  const scanBadge = (booking) => {
    const { used, qty, remaining, exhausted, overscanned } =
      computeScanInfo(booking);

    if (overscanned) {
      return (
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium bg-orange-50 text-orange-700 relative group">
          <ExclamationTriangleIcon className="w-4 h-4 text-orange-600" />
          <span>
            {used} / {qty} — Over Scanned
          </span>
          <InformationCircleIcon className="w-3.5 h-3.5 text-orange-500 ml-0.5 cursor-help" />
          <div className="absolute hidden group-hover:block bg-gray-900 text-white text-[11px] rounded-md py-1 px-2 -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap shadow-lg z-20">
            Scanned more times than total tickets.
          </div>
        </div>
      );
    }

    if (exhausted) {
      return (
        <span
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700"
          title="All tickets have been scanned."
        >
          <CheckCircleIcon className="w-4 h-4 text-gray-500" />
          {used} / {qty} — All Used
        </span>
      );
    }

    return (
      <span
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium bg-green-50 text-green-700"
        title={`${remaining} scan${remaining > 1 ? "s" : ""} remaining`}
      >
        <PlayCircleIcon className="w-4 h-4 text-green-600" />
        {used} / {qty} — {remaining} Left
      </span>
    );
  };
  const confirmManualVerify = async () => {
    if (!selectedBooking) return;
    setConfirming(true);
    setVerifying(true);
    setConfirmError(null);

    const { qty, used, remaining } = computeScanInfo(selectedBooking);
    if (remaining <= 0) {
      setConfirmError("No remaining scans available.");
      setVerifying(false);
      return;
    }

    const toScan = Math.min(selectedCount || 1, remaining);
    const idempotencyKey = confirmKeyRef.current || uuidv4();
    confirmKeyRef.current = idempotencyKey;

    const prevScanCount = selectedBooking.scan_count || 0;
    const optimisticBooking = {
      ...selectedBooking,
      scan_count: prevScanCount + toScan,
    };
    setSelectedBooking(optimisticBooking);

    setBookings((list) =>
      list.map((b) =>
        b.booking_id === optimisticBooking.booking_id ? optimisticBooking : b
      )
    );

    try {
      await new Promise((resolve, reject) => {
        scannerApi.confirmTicket({
          payload: {
            qrCodeData: selectedBooking.booking_id,
            count: toScan,
            idempotencyKey,
          },
          success: (res) => {
            confirmKeyRef.current = null;
            resolve(res);
            setAdditionalData(res?.data || null);
          },
          error: (err) => reject(err),
        });
      });

      await fetchBookings?.();
      setShowSuccessAnimation(true);
      showToast("success", `Confirmed ${toScan} scan(s).`);
      setVerifyOpen(false);
    } catch (err) {
      if (err?.response) {
        confirmKeyRef.current = null;
      }
      setSelectedBooking((s) => ({ ...s, scan_count: prevScanCount }));
      setBookings((list) =>
        list.map((b) =>
          b.booking_id === selectedBooking.booking_id
            ? { ...b, scan_count: prevScanCount }
            : b
        )
      );

      const payload = err?.response?.data || {};
      setConfirmError(
        payload?.message ||
          payload?.error ||
          "Network error. Retry will use the same request key."
      );
    } finally {
      setVerifying(false);
      setConfirming(false);
    }
  };

  const handleSuccessClose = () => {
    if (autoTimerRef.current) {
      clearInterval(autoTimerRef.current);
      autoTimerRef.current = null;
    }
    autoStartRef.current = null;
    setAutoProgress(100);
    setAutoDismissSeconds(0);

    setShowSuccessAnimation(false);
    setAdditionalData(null);

    fetchBookings();
  };

  const handleErrorClose = () => {
    setShowError(false);
    setAdditionalData(null);
    setErrorMessage("");
    fetchBookings();
  };

  const statusBadge = (status) => {
    if (!status) return null;
    const map = {
      succeeded: { label: "Paid", className: "bg-green-50 text-green-700" },
      pending: { label: "Pending", className: "bg-yellow-50 text-yellow-700" },
      failed: { label: "Failed", className: "bg-red-50 text-red-700" },
    };
    return map[status] ? (
      <span
        className={`px-2 py-1 rounded text-xs font-medium ${map[status].className}`}
      >
        {map[status].label}
      </span>
    ) : (
      <span className="px-2 py-1 rounded text-xs bg-gray-50 text-gray-700">
        {status}
      </span>
    );
  };

  const TableSkeleton = () => (
    <div className="animate-pulse space-y-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="h-12 bg-gray-100 rounded" />
      ))}
    </div>
  );

  return (
    <>
      <MetaData title="Bookings | TicketVerz Scanner" />
      <div className="max-w-6xl mx-auto p-4 md:p-8">
        <div>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold" style={{ color: BRAND }}>
                Manual Verification — Bookings
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Lookup bookings for an event and verify manually.
              </p>
            </div>

            <div className="w-full md:w-auto">
              <div className="flex flex-col sm:flex-row sm:items-center sm:gap-3 gap-3">
                <div className="w-full sm:w-80">
                  <Listbox
                    value={eventId}
                    onChange={(val) => {
                      setEventId(val);
                      setPage(1);
                    }}
                  >
                    <div className="relative">
                      <Listbox.Button className="relative w-full cursor-default rounded border bg-white py-2 pl-3 pr-10 text-left shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[#1d275f] text-sm flex items-center justify-between">
                        <div className="flex-1 min-w-0 pr-2">
                          <p
                            className="
        text-gray-900 font-medium
        truncate sm:truncate md:truncate 
        text-sm 
        sm:max-w-[80%]
        max-w-full
        sm:whitespace-nowrap
        whitespace-normal
        leading-tight
      "
                            title={
                              eventId
                                ? events.find(
                                    (e) =>
                                      String(e.content_id) === String(eventId)
                                  )?.title || eventId
                                : "Select event..."
                            }
                          >
                            {eventId
                              ? events.find(
                                  (e) =>
                                    String(e.content_id) === String(eventId)
                                )?.title || eventId
                              : "Select event..."}
                          </p>

                          <p className="text-xs text-gray-500 sm:hidden mt-0.5 line-clamp-2 leading-snug">
                            {eventId
                              ? events.find(
                                  (e) =>
                                    String(e.content_id) === String(eventId)
                                )?.address || "Select event..."
                              : ""}
                          </p>
                        </div>

                        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                          <FiChevronDown className="h-4 w-4 text-gray-500" />
                        </span>
                      </Listbox.Button>

                      <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                      >
                        <Listbox.Options className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                          <div className="px-3 pb-2">
                            <input
                              placeholder="Search events..."
                              value={eventSearch}
                              onChange={(e) => setEventSearch(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") fetchEvents();
                              }}
                              className="w-full rounded border px-2 py-1 text-sm"
                            />
                            <div className="text-xs text-gray-400 mt-1">
                              Press Enter to search
                            </div>
                          </div>

                          {eventsLoading && (
                            <div className="px-3 py-2 text-sm text-gray-500">
                              Loading events…
                            </div>
                          )}

                          {!eventsLoading && events.length === 0 && (
                            <div className="px-3 py-2 text-sm text-gray-500">
                              No events available.
                            </div>
                          )}

                          {!eventsLoading &&
                            events.map((ev) => (
                              <Listbox.Option
                                key={
                                  ev.content_id ||
                                  ev.event_id ||
                                  ev.movie_id ||
                                  ev.id
                                }
                                value={String(
                                  ev.content_id ||
                                    ev.event_id ||
                                    ev.movie_id ||
                                    ev.id
                                )}
                                as={Fragment}
                              >
                                {({ active, selected }) => (
                                  <li
                                    className={`cursor-pointer select-none px-3 py-2 ${
                                      active ? "bg-gray-100" : ""
                                    }`}
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="truncate">
                                        <div className="text-sm font-medium text-gray-800">
                                          {ev.title}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                          {ev.address || ev.city || ""}
                                        </div>
                                      </div>
                                      {selected && (
                                        <span className="text-xs text-[#1d275f] font-medium">
                                          Selected
                                        </span>
                                      )}
                                    </div>
                                  </li>
                                )}
                              </Listbox.Option>
                            ))}
                        </Listbox.Options>
                      </Transition>
                    </div>
                  </Listbox>
                  {eventsError && (
                    <div className="mt-1 text-xs text-red-500">
                      {eventsError}
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center bg-white border rounded shadow-sm px-3 py-2">
                    <FiSearch className="text-gray-400 mr-2" />
                    <input
                      className="w-full outline-none text-sm"
                      placeholder="Search by name / booking id / email"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      aria-label="Search bookings"
                    />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
                  <button
                    onClick={() => {
                      setPage(1);
                      fetchBookings();
                    }}
                    className="w-full sm:w-auto px-4 py-2 rounded bg-[#1d275f] text-white text-sm hover:bg-[#151d49] transition"
                    aria-label="Refresh bookings"
                  >
                    Refresh
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {errorBanner && (
          <div className="mb-4 p-3 rounded bg-red-50 text-red-700">
            {errorBanner}
          </div>
        )}

        <div className="bg-white rounded shadow p-4 mt-5">
          {loading ? (
            <TableSkeleton />
          ) : bookings.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No bookings found for this event.
            </div>
          ) : (
            <>
              <div className="hidden md:block">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 border-b">
                      <th className="py-3">Booking ID</th>
                      <th className="py-3">Name</th>
                      <th className="py-3">Email</th>
                      <th className="py-3">Qty</th>
                      <th className="py-3">Payment</th>
                      <th className="py-3">Scans</th>
                      <th className="py-3">Scan Status</th>
                      <th className="py-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((b) => (
                      <tr
                        key={b.booking_id}
                        className="border-b last:border-b-0 hover:bg-gray-50"
                      >
                        <td className="py-3 text-xs font-mono text-gray-800 line-clamp-2">
                          {b.booking_id}
                        </td>
                        <td className="py-3">
                          <div className="font-medium">
                            {b.first_name} {b.last_name}
                          </div>
                        </td>
                        <td className="py-3 text-xs line-clamp-2">{b.email}</td>
                        <td className="py-3">{b.quantity}</td>
                        <td className="py-3">{statusBadge(b.paymentStatus)}</td>
                        {(() => {
                          return <td className="py-3">{scanBadge(b)}</td>;
                        })()}
                        <td className="py-3">
                          <span
                            className={`px-2 py-1 rounded text-xs ${
                              b.scan_status === 1
                                ? "bg-green-50 text-green-700"
                                : "bg-gray-50 text-gray-700"
                            }`}
                          >
                            {b.scan_status === 1 ? "scanned" : "not scanned"}
                          </span>
                        </td>
                        <td className="py-3 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => openVerifyModal(b)}
                              className="px-3 py-1 text-sm rounded bg-[#1d275f] text-white hover:bg-[#151d49]"
                            >
                              Verify
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="mt-4 flex items-center justify-between">
                  <div className="text-sm text-gray-600">Total: {total}</div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      className="px-3 py-1 border rounded"
                      disabled={page <= 1}
                    >
                      Prev
                    </button>
                    <div className="px-3 py-1 border rounded text-sm">
                      {page}
                    </div>
                    <button
                      onClick={() => setPage((p) => p + 1)}
                      className="px-3 py-1 border rounded"
                      disabled={bookings.length < perPage}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>

              {/* Mobile list — single-column card, left-aligned content, actions below (mobile-friendly) */}
              <div className="md:hidden space-y-3">
                {bookings.map((b) => (
                  <div
                    key={b.booking_id}
                    className="bg-white rounded border p-3 shadow-sm overflow-hidden"
                  >
                    {/* Content row — we force min-w-0 so line-clamp / truncate work correctly */}
                    <div className="flex flex-col gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-gray-900">
                          {b.first_name} {b.last_name}
                        </div>

                        <div className="text-xs text-gray-500 mt-1 line-clamp-2 break-words min-w-0">
                          {b.email}
                        </div>

                        <div className="mt-2 text-xs text-gray-600">
                          Booking:{" "}
                          <span className="font-mono text-xs line-clamp-2 break-words min-w-0">
                            {b.booking_id}
                          </span>
                        </div>

                        <div className="mt-3">
                          <ScanInfoMobile booking={b} />
                        </div>
                      </div>

                      {/* Actions row — right-aligned on wide mobile, full-width on narrow */}
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-xs">
                          {statusBadge(b.paymentStatus)}
                        </div>

                        {/* Button: prevent overflow (flex-none) and keep consistent sizing */}
                        <div className="flex-shrink-0">
                          <button
                            onClick={() => openVerifyModal(b)}
                            className="px-3 py-1 rounded text-sm text-white w-28 flex items-center justify-center"
                            style={{ backgroundColor: BRAND }}
                          >
                            Verify
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                <div className="mt-2 flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="flex-1 px-3 py-2 border rounded"
                  >
                    Prev
                  </button>
                  <div className="px-3 py-2 border rounded">{page}</div>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    className="flex-1 px-3 py-2 border rounded"
                    disabled={bookings.length < perPage}
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <Transition appear show={verifyOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-40"
          onClose={() => setVerifyOpen(false)}
        >
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black bg-opacity-30" />

          {/* Centered modal container, panel is constrained for desktop/mobile */}
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200 transform"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
            >
              <Dialog.Panel className="w-full max-w-md bg-white rounded-lg shadow-lg p-6">
                {/* Header: title (left) + close (right) */}
                <div className="flex items-start justify-between">
                  <Dialog.Title className="text-lg font-semibold text-gray-900 text-left">
                    Confirm Manual Verification
                  </Dialog.Title>

                  <button
                    aria-label="Close"
                    onClick={() => setVerifyOpen(false)}
                    className="ml-4 text-gray-400 hover:text-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-200"
                  >
                    <span className="text-xl leading-none">✕</span>
                  </button>
                </div>

                {/* Intro copy */}
                <div className="mt-3 text-sm text-gray-600 text-left">
                  You are about to manually verify this booking. This action
                  will mark the ticket(s) as scanned.
                </div>

                {selectedBooking && (
                  <div className="mt-5 text-sm text-gray-700 space-y-4 text-left">
                    {/* Name */}
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide">
                        Name
                      </div>
                      <div className="font-medium text-gray-900">
                        {selectedBooking.first_name} {selectedBooking.last_name}
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide">
                        Email
                      </div>
                      <div className="font-medium text-gray-900 break-all">
                        {selectedBooking.email}
                      </div>
                    </div>

                    {/* Booking ID */}
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide">
                        Booking ID
                      </div>
                      <div className="font-mono font-semibold text-gray-900">
                        {selectedBooking.booking_id}
                      </div>
                    </div>

                    {/* Scan counts */}
                    {(() => {
                      const { qty, used, remaining, exhausted } =
                        computeScanInfo(selectedBooking);
                      return (
                        <div className="pt-2 border-t border-gray-200 space-y-2">
                          <div>
                            <div className="text-xs text-gray-500 uppercase tracking-wide">
                              Quantity
                            </div>
                            <div className="font-medium text-gray-900">
                              {qty}
                            </div>
                          </div>

                          <div>
                            <div className="text-xs text-gray-500 uppercase tracking-wide">
                              Scanned
                            </div>
                            <div className="font-medium text-gray-900">
                              {used}
                            </div>
                          </div>

                          <div>
                            <div className="text-xs text-gray-500 uppercase tracking-wide">
                              Remaining
                            </div>
                            <div className="font-medium text-gray-900">
                              {remaining}
                            </div>
                          </div>

                          {exhausted && (
                            <div className="inline-block px-2 py-1 rounded text-xs bg-yellow-50 text-yellow-700">
                              Fully Scanned
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
                {/* WHEN NO REMAINING — show informative card (left-aligned content) */}
                {selectedBooking &&
                computeScanInfo(selectedBooking).remaining <= 0 ? (
                  <div
                    className="mt-4 p-3 rounded border bg-yellow-50 text-sm text-yellow-800 text-left"
                    role="status"
                    aria-live="polite"
                  >
                    <strong>Info:</strong> All tickets for this booking are
                    already scanned.
                    <div className="mt-2 text-xs text-gray-600">
                      If this looks incorrect, check the booking in the admin
                      panel or contact the organizer.
                    </div>
                  </div>
                ) : (
                  selectedBooking && (
                    <div className="mt-4 text-left">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        How many attendees are you verifying?
                      </label>

                      <div className="flex items-center gap-3">
                        <button
                          onClick={() =>
                            setSelectedCount((c) => Math.max(1, c - 1))
                          }
                          className="p-2 rounded border bg-white disabled:opacity-50"
                          disabled={verifying}
                          aria-label="Decrease attendees"
                        >
                          −
                        </button>

                        <input
                          type="number"
                          min={1}
                          max={computeScanInfo(selectedBooking).remaining}
                          value={selectedCount}
                          onChange={(e) => {
                            const v = Number(e.target.value || 0);
                            const clamped = Math.max(
                              1,
                              Math.min(
                                computeScanInfo(selectedBooking).remaining,
                                v
                              )
                            );
                            setSelectedCount(clamped);
                          }}
                          className="w-20 text-center px-3 py-2 border rounded"
                          aria-label="Number of attendees"
                          disabled={verifying}
                        />

                        <button
                          onClick={() =>
                            setSelectedCount((c) =>
                              Math.min(
                                computeScanInfo(selectedBooking).remaining,
                                c + 1
                              )
                            )
                          }
                          className="p-2 rounded border bg-white disabled:opacity-50"
                          disabled={verifying}
                          aria-label="Increase attendees"
                        >
                          +
                        </button>

                        <div className="text-sm text-gray-500">
                          (max {computeScanInfo(selectedBooking).remaining})
                        </div>
                      </div>

                      {/* actions */}
                      <div className="mt-6 flex items-center justify-end gap-3">
                        <button
                          onClick={() => setVerifyOpen(false)}
                          className="px-4 py-2 rounded border bg-white text-sm"
                          disabled={verifying}
                        >
                          Cancel
                        </button>

                        <button
                          onClick={confirmManualVerify}
                          className={`px-4 py-2 rounded text-sm text-white ${
                            verifying
                              ? "bg-gray-400"
                              : "bg-[#1d275f] hover:bg-[#151d49]"
                          }`}
                          disabled={verifying}
                        >
                          {verifying
                            ? "Verifying..."
                            : `Confirm (${selectedCount})`}
                        </button>
                      </div>
                    </div>
                  )
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
      {/* Success Popup */}
      <Transition appear show={showSuccessAnimation} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={handleSuccessClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 flex items-center justify-center p-2 md:p-4 overflow-y-auto">
            <div className="w-full max-w-sm md:max-w-md p-4 md:p-6 bg-white rounded-lg shadow-xl">
              <div className="text-center">
                <CheckCircleIcon className="h-12 w-12 md:h-16 md:w-16 text-green-500 mx-auto mb-3" />
                <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-3">
                  Access Granted!
                </h3>

                {additionalData && (
                  <div className="text-gray-700 text-sm md:text-base mb-4 space-y-1 text-left">
                    <h3 className="text-black font-semibold text-center mb-3 md:text-lg">
                      {additionalData?.message}
                    </h3>
                    <p>
                      <span className="font-medium">
                        {additionalData?.bookingData?.type} Name:
                      </span>{" "}
                      <b>{additionalData?.bookingData?.title}</b>
                    </p>
                    <p>
                      <span className="font-medium">Name:</span>{" "}
                      <b>
                        {additionalData?.bookingData?.first_name}{" "}
                        {additionalData?.bookingData?.last_name || ""}
                      </b>
                    </p>
                    <p>
                      <span className="font-medium">Email:</span>{" "}
                      <b>{additionalData?.bookingData?.email}</b>
                    </p>
                    <p>
                      <span className="font-medium">Quantity:</span>{" "}
                      <b>{additionalData?.bookingData?.quantity}</b>
                    </p>
                    <p>
                      <span className="font-medium">Date:</span>{" "}
                      <b>
                        {additionalData?.bookingData?.event_date?.split("T")[0]}
                      </b>
                    </p>
                    <p>
                      <span className="font-medium">Booking ID:</span>{" "}
                      <b>{additionalData?.bookingData?.booking_id}</b>
                    </p>

                    {additionalData?.bookingData?.type === "Movie" ? (
                      <>
                        <p className="font-medium mt-2">Seats:</p>
                        <ul className="list-disc pl-4">
                          {Object.entries(
                            Object.entries(
                              JSON.parse(
                                additionalData?.bookingData?.seat_numbers ||
                                  "{}"
                              )
                            ).reduce((acc, [seat, type]) => {
                              acc[type] = acc[type] || [];
                              acc[type].push(seat);
                              return acc;
                            }, {})
                          ).map(([type, seats]) => (
                            <li
                              key={type}
                              className="text-[#1d275f] font-semibold"
                            >
                              {type}: {seats.join(", ")}
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : (
                      <>
                        <div className="font-medium text-gray-800 mb-1">
                          Ticket Details
                        </div>
                        {(additionalData?.bookingData?.tickets || []).map(
                          (ticket, index) => (
                            <div
                              key={index}
                              className="flex justify-between items-center rounded-lg border px-3 py-2 bg-gray-50"
                            >
                              <span className="font-semibold text-gray-900">
                                {ticket.ticket_name}
                              </span>
                              <span className="font-semibold text-gray-600 text-md">
                                x {ticket.quantity}
                              </span>
                            </div>
                          )
                        )}
                      </>
                    )}
                  </div>
                )}

                <button
                  className="bg-[#1d275f] text-white px-4 py-2 md:px-8 md:py-3 rounded-lg shadow hover:bg-[#151d49] transition duration-200 mt-4 w-full"
                  onClick={handleSuccessClose}
                >
                  Proceed
                </button>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition>
      <Transition appear show={showError} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={handleErrorClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 flex items-center justify-center p-2 md:p-4 overflow-y-auto">
            <div className="w-full max-w-sm md:max-w-md p-4 md:p-6 bg-white rounded-lg shadow-xl">
              <div className="text-center">
                <ExclamationCircleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-red-600 mb-4">
                  Error!
                </h3>
                <p className="text-gray-700 mb-4">{errorMessage}</p>
                {additionalData && (
                  <div className="text-gray-700 text-sm md:text-base mb-4 space-y-1 text-left">
                    <p>
                      <span className="font-medium">
                        {additionalData?.bookingData?.type} Name:
                      </span>{" "}
                      <b>{additionalData?.bookingData?.title}</b>
                    </p>
                    <p>
                      <span className="font-medium">Name:</span>{" "}
                      <b>
                        {additionalData?.bookingData?.first_name}{" "}
                        {additionalData?.bookingData?.last_name || ""}
                      </b>
                    </p>
                    <p>
                      <span className="font-medium">Email:</span>{" "}
                      <b>{additionalData?.bookingData?.email}</b>
                    </p>
                    <p>
                      <span className="font-medium">Quantity:</span>{" "}
                      <b>{additionalData?.bookingData?.quantity}</b>
                    </p>
                    <p>
                      <span className="font-medium">Date:</span>{" "}
                      <b>
                        {additionalData?.bookingData?.event_date?.split("T")[0]}
                      </b>
                    </p>
                    <p>
                      <span className="font-medium">Booking ID:</span>{" "}
                      <b>{additionalData?.bookingData?.booking_id}</b>
                    </p>

                    {additionalData?.bookingData?.type === "Movie" ? (
                      <>
                        <p className="font-medium mt-2">Seats:</p>
                        <ul className="list-disc pl-4">
                          {Object.entries(
                            Object.entries(
                              JSON.parse(
                                additionalData?.bookingData?.seat_numbers ||
                                  "{}"
                              )
                            ).reduce((acc, [seat, type]) => {
                              acc[type] = acc[type] || [];
                              acc[type].push(seat);
                              return acc;
                            }, {})
                          ).map(([type, seats]) => (
                            <li
                              key={type}
                              className="text-[#1d275f] font-semibold"
                            >
                              {type}: {seats.join(", ")}
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : (
                      <>
                        <p className="font-medium mt-2">Ticket Details:</p>
                        <ul className="list-disc pl-4">
                          {(additionalData?.bookingData?.tickets || []).map(
                            (ticket, index) => (
                              <li
                                key={index}
                                className="text-[#1d275f] font-semibold"
                              >
                                {ticket.ticket_name} x {ticket.quantity}
                              </li>
                            )
                          )}
                        </ul>
                      </>
                    )}
                  </div>
                )}
                <button
                  className="bg-red-500 text-white px-8 py-3 rounded-lg shadow-xl hover:bg-red-600 transition duration-200"
                  onClick={handleErrorClose}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition>
      {/* Toast */}
      <div
        aria-live="assertive"
        className="fixed inset-0 flex items-end px-4 py-6 pointer-events-none sm:p-6"
      >
        <div className="w-full flex flex-col items-center space-y-4 sm:items-end">
          <Transition show={!!toast} as={Fragment}>
            <div
              className={`max-w-sm w-full bg-white shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden ${
                toast?.type === "error"
                  ? "border-l-4 border-red-500"
                  : "border-l-4 border-blue-500"
              }`}
            >
              <div className="p-3">
                <p className="text-sm text-gray-900">{toast?.message}</p>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </>
  );
}
